<template>
  <CardWithTitle
    :item-id="itemId"
    :request-status="requestStatus"
    :options-data="cardOptionsWithCSVExportOption"
    :has-custom-filter="hasCustomFilter"
    @call-option-menu="callOptionMenu"
    @reload="$emit('reload', $event)"
  >
    <v-row
      no-gutters
      class="bar-chart-container"
    >
      <HighCharts
        v-if="hasData"
        :ref="chartRef"
        :key="itemId"
        class="bar-chart"
        :options="chartOptions"
        :callback="highchartsCallBack"
      />
      <ChartLegendTable
        :legend-data="localizedLegendData"
      />
    </v-row>

    <template #top-right-slot>
      <template v-if="hasCustomFilter">
        <DsButton
          v-tooltip="filterButton"
          color="tertiary"
          name="filter"
          :icon="true"
          @click="openFilter"
        />
      </template>
      <GranularityDropDown
        v-if="showGranularity"
        v-model="granularity"
        :item-id="itemId"
        @reload="$emit('reload', $event)"
      />

      <Badge
        v-if="hasCustomFilter && isEditor"
        type="info"
        :tooltip="$t('components.Badge.label.tooltip.info')"
        :value="$t('components.Badge.label.icon.key')"
        :preset="true"
      />
      <DsButton
        v-if="showDescription"
        v-tooltip="infoLabel"
        color="tertiary"
        name="questions"
        :icon="true"
        @click="openDialog"
      />
    </template>
  </CardWithTitle>
</template>

<script>
import { Chart } from 'highcharts-vue';
import highChartsBasics from '@/mixins/highcharts-basics';
import CardWithTitle from '@/components/cards/models/CardWithTitle';
import cardsModel from '@/mixins/cards-model';
import exportModel from '@/mixins/export-model';
import merge from 'lodash/merge';
import GranularityDropDown from '@/components/GranularityDropDown';
import Badge from '@/components/Badge';
import { DsButton } from '@ds/components';
import ChartLegendTable from '@/components/cards/models/ChartLegendTable';

export default {
  name: 'ColumnChart',
  components: {
    HighCharts: Chart,
    CardWithTitle,
    GranularityDropDown,
    Badge,
    DsButton,
    ChartLegendTable,
  },
  mixins: [
    highChartsBasics,
    cardsModel,
    exportModel,
  ],
  props: {
    requestStatus: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    itemId: {
      type: [String, Number],
      required: true,
    },
    categories: {
      type: [Array, Object],
      default: () => ([]),
    },
    series: {
      type: [Array, Object],
      default: () => ([]),
    },
    cardOptions: {
      type: [Array, Object],
      default: () => ([]),
    },
    showGranularity: {
      type: Boolean,
      default: true,
    },
    openDialog: {
      type: Function,
      default: () => {},
    },
    showDescription: {
      type: Boolean,
      required: false,
    },
    hasCustomFilter: {
      type: Boolean,
      default: false,
    },
    openFilter: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      granularity: null,
      legendData: null,
    };
  },
  computed: {
    localizedLegendData() {
      if (!this.legendData) return null;
      return this.legendData.map((hcSeries, i) => {
        const propSeries = Array.isArray(this.series) ? this.series[i] : null;
        return {
          color: hcSeries.color,
          visible: hcSeries.visible,
          name: propSeries ? propSeries.name : hcSeries.name,
          setVisible: hcSeries.setVisible ? hcSeries.setVisible.bind(hcSeries) : undefined,
          setState: hcSeries.setState ? hcSeries.setState.bind(hcSeries) : undefined,
        };
      });
    },
    infoLabel() {
      return this.$t('components.OperatorsLogs.infoLabel');
    },
    filterButton() {
      return this.$t('components.OperatorsLogs.filterLabel');
    },
    hasData() {
      return Object.keys(this.series).length;
    },
    chartOptions() {
      if (!this.hasData) {
        return {};
      }

      const customOptions = {
        chart: {
          type: 'column',
        },
        xAxis: {
          lineWidth: 0,
          tickLength: 0,
        },
        yAxis: {
          gridLineColor: 'transparent',
          min: 0,
          title: '',
          tickColor: 'transparent',
          labels: {
            enabled: false,
          },
          stackLabels: {
            enabled: true,
          },
        },
        plotOptions: {
          column: {
            borderColor: 'black',
            borderWidth: 0.5,
            pointPadding: 0,
          },
          series: {
            stacking: 'normal',
          },
        },
        series: this.series,
      };

      return merge({}, this.highchartsBasics, this.highchartTheme, customOptions, this.cardOptions, { legend: { enabled: false } });
    },
  },
  watch: {
    chartOptions() {
      this.$nextTick(() => {
        this.highchartsCallBack(this.$refs[this.chartRef]?.chart);
      });
    },
  },
  methods: {
    highchartsCallBack(chart) {
      this.legendData = chart?.series;
    },
  },
};
</script>

<style scoped lang="scss">
  .chart {
    width: 100%;
  }

  .bar-chart-container {
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
    overflow: hidden;
    justify-content: flex-start;
  }

  .bar-chart {
    width: 100%;
    height: calc(100% - 72px);
  }
</style>
